'use client';
import { useId } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Scrollbar, A11y } from 'swiper/modules';
import NextImage from 'next/image';
import { cn } from 'lib/utils';
import { type LinkField, type ImageField } from 'lib/types';
import {
  type BloomProps,
  Bloom,
  CarouselNavigation,
  Icon,
  Button,
  Wrapper,
} from 'ui';

export type ImageCardsCarouselProps = {
  className?: string;
  title?: string;
  items: {
    image: ImageField;
    label: string;
    link?: LinkField;
  }[];
  bloom?: BloomProps;
};

const ImageCardsCarousel = ({
  title,
  items,
  className,
  bloom,
}: ImageCardsCarouselProps) => {
  const id = useId().replaceAll(':', ''); // Remove colons as an SSR-friendly alternative to CSS.escape()

  return (
    <Wrapper
      as="section"
      variant="page-full"
      className={cn('relative pr-0', className)}
      aria-labelledby={title ? `${id}-title` : undefined}
    >
      <Bloom {...bloom} />

      {title && (
        <h2
          id={`${id}-title`}
          className="relative z-2 font-mono text-eyebrow uppercase mb-6 md:md-10"
        >
          {title}
        </h2>
      )}

      <Swiper
        modules={[Navigation, Scrollbar, A11y]}
        navigation={{
          nextEl: `#${id}-next`,
          prevEl: `#${id}-prev`,
        }}
        scrollbar={{
          el: `#${id}-scrollbar`,
          draggable: false,
        }}
        spaceBetween={16}
        slidesPerView={1.15}
        breakpoints={{
          // when window width is >= 360px
          360: {
            slidesPerView: 1.25,
            spaceBetween: 16,
          },
          // when window width is >= 480px
          480: {
            slidesPerView: 1.5,
            spaceBetween: 16,
          },
          // when window width is >= 800px
          800: {
            slidesPerView: 2.25,
            spaceBetween: 24,
          },
          // when window width is >= 1024px
          1024: {
            slidesPerView: 2.75,
            spaceBetween: 24,
          },
        }}
        // onSlideChange={() => console.log('slide change')}
        // onSwiper={(swiper) => console.log(swiper)}
        className="z-2"
      >
        {items.map((item, index) => {
          const isExternalLink = item.link?.target === '_blank';

          return (
            <SwiperSlide key={index}>
              <article className="relative w-full h-95 p-4 md:p-6 flex flex-col justify-end">
                <NextImage
                  src={item.image.src}
                  alt={item.image.alt}
                  fill={true}
                  className="absolute w-full h-auto mb-4 object-cover rounded-lg"
                  role={!item.image.alt ? 'presentation' : undefined}
                />

                <div className="flex items-end justify-between gap-4">
                  <h3
                    id={`${id}-label-${index}`}
                    className="z-3 text-h4 grow max-w-84"
                  >
                    {item.label}
                  </h3>

                  {!!item.link && (
                    <Button
                      href={item.link.href}
                      target={item.link.target}
                      className="z-4 size-12 shrink-0 after:absolute after:inset-0 p-0"
                      variant="secondary"
                      aria-labelledby={`${id}-label-${index}`}
                      trackingProps={item.link.trackingProps}
                    >
                      <Icon
                        name={
                          isExternalLink
                            ? 'external-link-arrow'
                            : 'internal-link-arrow-right'
                        }
                        className={cn('h-auto shrink-0', {
                          'w-4': !isExternalLink,
                          'w-3.5': isExternalLink,
                        })}
                        role="presentation"
                      />
                    </Button>
                  )}

                  {/* Color Gradient */}
                  <div
                    className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-graphite-950/50 to-transparent rounded-b-lg"
                    aria-hidden="true"
                  />

                  {/* Blur Gradient */}
                  <div
                    className="z-2 absolute bottom-0 left-0 w-full h-1/2 mask-[linear-gradient(180deg,transparent,black,black)] backdrop-blur rounded-b-lg"
                    aria-hidden="true"
                  />
                </div>
              </article>
            </SwiperSlide>
          );
        })}
      </Swiper>

      <CarouselNavigation
        scrollbarId={`${id}-scrollbar`}
        prevId={`${id}-prev`}
        nextId={`${id}-next`}
      />
    </Wrapper>
  );
};

export default ImageCardsCarousel;
